/* =============================================================================
   Schriftgrösse und Startposition automatisch bestimmen

   Bisher stand in Home.js eine von Hand gepflegte Tabelle: Moritz 48, Lisa 46,
   Anton 40, alles andere 50. Nachgemessen führt das zu sehr ungleichen
   Ergebnissen — die x-Höhe, also das, was das Auge als "Schriftgrösse" liest,
   schwankte zwischen 11,6 px (Moritz) und 21,0 px (Anton). Fast Faktor zwei bei
   nominell ähnlicher Einstellung. Jede neu hinzukommende Schrift bekam blind 50
   und sass dann irgendwo.

   Hier wird stattdessen gemessen und gerechnet:

   1. GRÖSSE — jede Schrift wird so skaliert, dass ihre x-Höhe gleich ist.
      Danach wirken alle Schriften gleich gross, egal wie ihre Datei gebaut ist.

   2. START — die Grundlinie der ersten Zeile wird auf eine Papierlinie gelegt,
      und zwar bei jeder Schrift auf dieselbe.

   Gemessen wird über Canvas und ein Proben-Element, nicht über die Font-
   Tabellen: fontBoundingBox liefert im Canvas für alle Schriften denselben
   Näherungswert und ist unbrauchbar.
   ========================================================================== */

const metrikMerker = new Map();
const grundlinienMerker = new Map();

/* Auf welcher Papierlinie der Text beginnt — für alle Schriften dieselbe.

   Naheliegender wäre die oberste Linie. Die geht aber nicht für jede Schrift:
   über der Grundlinie braucht es Platz für die Oberlängen. Die Startlinie
   deshalb nicht je Schrift zu wählen, sondern fest — sobald sie von den
   Oberlängen abhängt, beginnt der Text je nach Schrift auf einer anderen
   Zeile, und genau das soll ja weg. */
const START_LINIE = 1;

/* Wie hoch ein kleines "x" bei 1 px Schriftgrösse ist, dazu Ober- und
   Unterlänge. Gemessen an der tatsächlichen Tinte. */
export function metrikenVon(fontFamily) {
  if (metrikMerker.has(fontFamily)) return metrikMerker.get(fontFamily);

  const standard = { xHoehe: 0.27, oberlaenge: 0.41, unterlaenge: 0.05, gemessen: false };

  /* Nur die erste Familie messen, nicht die ganze Kette. Übergeben wird etwas
     wie '"Sebastian", "SebastianBackup"' — gemessen werden sollen aber die
     Metriken der Hauptschrift, nicht die der Ersatzschrift. */
  const ersteFamilie = String(fontFamily).split(",")[0].trim();
  if (!ersteFamilie) return standard;

  /* Erst prüfen, ob die Schrift schon geladen ist. measureText fällt sonst
     stillschweigend auf eine Systemschrift zurück und liefert deren Werte —
     plausibel aussehend, aber falsch. Dann bekämen alle Schriften dieselbe
     Grösse, weil jedes Mal dieselbe Ersatzschrift vermessen würde. */
  try {
    if (!document.fonts || !document.fonts.check(`100px ${ersteFamilie}`)) {
      return standard; // bewusst nicht merken — nach dem Laden neu messen
    }
  } catch {
    return standard;
  }

  let werte = standard;
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.font = `100px ${ersteFamilie}`;
    const x = ctx.measureText("x").actualBoundingBoxAscent;
    const h = ctx.measureText("h").actualBoundingBoxAscent;
    const g = ctx.measureText("g").actualBoundingBoxDescent;

    if (x > 5 && x < 95 && h > 5) {
      werte = {
        xHoehe: x / 100,
        oberlaenge: h / 100,
        unterlaenge: Math.max(0, g) / 100,
        gemessen: true,
      };
    }
  } catch {
    /* bleibt beim Standard */
  }

  if (werte.gemessen) metrikMerker.set(fontFamily, werte);
  return werte;
}

/* Schriftgrösse, bei der diese Schrift dieselbe x-Höhe hat wie jede andere.

   Der Zielwert ist an Sebastian ausgerichtet, der meistbenutzten Schrift: mit
   der gewohnten Grösse 50 px misst deren x-Höhe 8,95 px, bezogen auf 35 px
   Zeilenabstand also rund 0,256. So bleibt das gewohnte Schriftbild dort
   erhalten, und die anderen Schriften ziehen darauf nach.

   Der Wert wirkt klein, weil er sich auf die tatsächliche x-Höhe ab der
   Grundlinie bezieht. Vor der Korrektur der Schriftdateien schwebten die
   Buchstaben über der Grundlinie, wodurch hier ein zu grosser Wert gemessen
   wurde — daher stand hier zwischenzeitlich 0,43. */
export function berechneSchriftgroesse(fontFamily, lineSpacing, zielAnteil = 0.256) {
  const m = metrikenVon(fontFamily);
  let groesse = (lineSpacing * zielAnteil) / m.xHoehe;

  /* Deckel: Ober- plus Unterlänge dürfen den Zeilenabstand nur wenig
     überschreiten, sonst berühren sich die Zeilen. */
  const gesamthoehe = (m.oberlaenge + m.unterlaenge) * groesse;
  const erlaubt = lineSpacing * 1.25;
  if (gesamthoehe > erlaubt) groesse *= erlaubt / gesamthoehe;

  return Math.round(groesse * 10) / 10;
}

/* Wo liegt die Grundlinie der ersten Zeile, gemessen vom oberen Rand des
   Textkastens? Hängt von Schrift, Grösse und Zeilenhöhe ab. */
export function messeGrundlinie(fontFamily, fontSizePx, lineHeightPx) {
  const schluessel = `${fontFamily}|${fontSizePx}|${lineHeightPx}`;
  if (grundlinienMerker.has(schluessel)) return grundlinienMerker.get(schluessel);

  // Wie bei den Metriken: solange die Schrift nicht geladen ist, misst der
  // Browser die Ersatzschrift. Dann lieber nicht merken und später neu messen.
  let schriftDa = false;
  try {
    const erste = String(fontFamily).split(",")[0].trim();
    schriftDa = !!(document.fonts && document.fonts.check(`${fontSizePx}px ${erste}`));
  } catch {
    schriftDa = false;
  }

  let wert = lineHeightPx * 0.8; // Notnagel, falls das Messen scheitert
  try {
    const kasten = document.createElement("div");
    kasten.style.cssText =
      `position:absolute;left:-9999px;top:0;visibility:hidden;` +
      `font-family:${fontFamily};font-size:${fontSizePx}px;line-height:${lineHeightPx}px;`;
    // Genauso gebaut wie der echte Text — ein inline-block je Zeichen
    kasten.innerHTML =
      `<span style="display:inline-block;line-height:0px;font-size:${fontSizePx}px;">H</span>` +
      `<span class="probe" style="display:inline-block;width:0;height:0;vertical-align:baseline;"></span>`;
    document.body.appendChild(kasten);
    const oben = kasten.getBoundingClientRect().top;
    const probe = kasten.querySelector(".probe").getBoundingClientRect().top;
    kasten.remove();
    if (isFinite(probe - oben)) wert = probe - oben;
  } catch {
    /* Notnagel bleibt */
  }

  if (schriftDa) grundlinienMerker.set(schluessel, wert);
  return wert;
}

/* Der obere Abstand des Textkastens, damit die Grundlinie der ersten Zeile
   genau auf einer Papierlinie sitzt. */
export function berechnePaddingTop(
  fontFamily,
  fontSizePx,
  lineHeightPx,
  ersteLinieVomKasten,
  linienAbstand,
  startLinie = START_LINIE
) {
  const grundlinie = messeGrundlinie(fontFamily, fontSizePx, lineHeightPx);
  const ziel = ersteLinieVomKasten + startLinie * linienAbstand;
  return Math.max(0, ziel - grundlinie);
}

/* =============================================================================
   Der Versatz zwischen Vorschau und Export

   Im Bild- und PDF-Export sass der Text tiefer als in der Vorschau, und zwar je
   Schrift unterschiedlich weit. Vorher stand dagegen eine feste Zahl (40 px) —
   die kann nur für eine einzige Schriftgrösse stimmen.

   Ursache ist html2canvas. Es setzt Text nicht über den Zeilenkasten des
   Browsers, sondern zeichnet ihn mit `fillText` auf eine Grundlinie, die es
   sich selbst misst (FontMetrics.parseMetrics). Diese Messung fällt in
   Schriftbot aus zwei Gründen ganz anders aus als die echte Textlage:

   1. Tailwinds Preflight setzt `img { display: block }`. html2canvas stellt für
      die Messung ein 1-px-Bild mit `vertical-align: baseline` neben eine
      Textprobe und liest die Differenz der beiden `offsetTop` ab. Als Block
      sitzt das Bild aber nicht mehr auf der Grundlinie, sondern rutscht unter
      die Textzeile — gemessen wird damit die Höhe des Zeilenkastens statt der
      Oberlänge.

   2. Der Messkasten hängt an `document.body` und erbt von dort
      `line-height: 1.65` (index.css). Dieser Zeilenkasten ist also 1,65-mal so
      hoch wie die Schriftgrösse — und nicht 35 px wie der echte Textkasten.

   Zusammen nimmt html2canvas die Grundlinie rund `(1,65 + 1,0)/2 · Schriftgrösse`
   unter der Oberkante des Zeichens an, wo tatsächlich `0,8 · Schriftgrösse`
   richtig wäre. Der Fehler wächst also mit der Schriftgrösse: bei Anton (21 px)
   sind es 13 px, bei Sebastian (50 px) 27 px, bei Moritz (76 px) 40 px. Genau
   daher der Eindruck, der Text sitze "je nach Schrift" woanders.

   Nachgerechnet wird das hier bewusst nicht — gemessen wird dasselbe, was
   html2canvas misst, mit dessen eigenem Verfahren. Ändert sich an den
   Stylesheets etwas (`line-height` am body, Tailwind-Preflight), ändert sich
   diese Messung mit.
   ========================================================================== */

const exportMerker = new Map();

/* Dasselbe 1x1-GIF, das html2canvas für seine Messung benutzt. */
const MESSBILD =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/* Die Grundlinie, auf die html2canvas zeichnet. Zeile für Zeile wie in dessen
   FontMetrics.parseMetrics aufgebaut — jede Abweichung hier verschiebt den
   Text im Export. */
function h2cGrundlinie(fontFamily, fontSizePx) {
  const kasten = document.createElement("div");
  const bild = document.createElement("img");
  const probe = document.createElement("span");

  kasten.style.visibility = "hidden";
  kasten.style.fontFamily = fontFamily;
  kasten.style.fontSize = `${fontSizePx}px`;
  kasten.style.margin = "0";
  kasten.style.padding = "0";
  kasten.style.whiteSpace = "nowrap";
  document.body.appendChild(kasten);

  bild.src = MESSBILD;
  bild.width = 1;
  bild.height = 1;
  bild.style.margin = "0";
  bild.style.padding = "0";
  bild.style.verticalAlign = "baseline";

  probe.style.fontFamily = fontFamily;
  probe.style.fontSize = `${fontSizePx}px`;
  probe.style.margin = "0";
  probe.style.padding = "0";
  probe.appendChild(document.createTextNode("Hidden Text"));

  kasten.appendChild(probe);
  kasten.appendChild(bild);
  const wert = bild.offsetTop - probe.offsetTop + 2;
  kasten.remove();
  return wert;
}

/* Wo die Oberkante eines Zeichens liegt, von der Grundlinie aus nach oben —
   das ist der Punkt, von dem html2canvas seine Grundlinie abträgt. Der Kasten
   ist genauso gebaut wie ein echtes Zeichen in A4Page. */
function cssOberlaenge(fontFamily, fontSizePx, lineHeightPx) {
  const kasten = document.createElement("div");
  kasten.style.cssText =
    `position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;` +
    `font-family:${fontFamily};font-size:${fontSizePx}px;line-height:${lineHeightPx}px;`;
  kasten.innerHTML =
    `<span class="zeichen" style="display:inline-block;line-height:0px;font-size:${fontSizePx}px;">H</span>` +
    `<span class="probe" style="display:inline-block;width:0;height:0;vertical-align:baseline;"></span>`;
  document.body.appendChild(kasten);

  let wert = fontSizePx * 0.8; // Notnagel: ascender 800 bei 1000 Geviert
  try {
    const bereich = document.createRange();
    bereich.selectNodeContents(kasten.querySelector(".zeichen"));
    const oben = bereich.getClientRects()[0].top;
    const grundlinie = kasten.querySelector(".probe").getBoundingClientRect().top;
    if (isFinite(grundlinie - oben)) wert = grundlinie - oben;
  } catch {
    /* Notnagel bleibt */
  }

  kasten.remove();
  return wert;
}

/* Um wie viele Pixel der Text im Export tiefer sitzt als in der Vorschau.
   Positiv = zu tief; genau diesen Wert muss der Export nach oben ausgleichen.

   `fontSizePx` ist die Grösse, mit der die Zeichen tatsächlich gesetzt werden —
   nicht die nominelle Schriftgrösse. In A4Page bekommt jedes Zeichen eine
   eigene, leicht kleinere Grösse; der Aufrufer reicht deshalb den Mittelwert
   herein. */
export function berechneExportVersatz(fontFamily, fontSizePx, lineHeightPx) {
  const schluessel = `${fontFamily}|${fontSizePx}|${lineHeightPx}`;
  if (exportMerker.has(schluessel)) return exportMerker.get(schluessel);

  // Wie bei den Metriken: mit ungeladener Schrift misst der Browser die
  // Ersatzschrift. Dann rechnen, aber nicht merken.
  let schriftDa = false;
  try {
    const erste = String(fontFamily).split(",")[0].trim();
    schriftDa = !!(document.fonts && document.fonts.check(`${fontSizePx}px ${erste}`));
  } catch {
    schriftDa = false;
  }

  let wert = 0;
  try {
    wert = h2cGrundlinie(fontFamily, fontSizePx) - cssOberlaenge(fontFamily, fontSizePx, lineHeightPx);
  } catch {
    wert = 0; // im Zweifel nicht verschieben
  }

  if (!isFinite(wert)) wert = 0;
  if (schriftDa) exportMerker.set(schluessel, wert);
  return wert;
}

export function leereSchriftMetrikCache() {
  metrikMerker.clear();
  grundlinienMerker.clear();
  exportMerker.clear();
}
